/**
 * Offline write queue for the worker PWA.
 *
 * Field workers regularly lose signal mid-job (basements, lifts, parking
 * structures). Status changes (Accept / On My Way / Start / Complete) must
 * still feel instant when offline and replay when the device reconnects.
 *
 * Scope (v1):
 *   - Only PATCH /jobs/:id status changes are queued. Proof uploads need
 *     a live S3 presigned URL (15-minute lifetime) so they always require
 *     a connection.
 *   - Idempotency-Key on the original POST/PATCH protects against duplicate
 *     replay (see services/api/src/lib/idempotency.ts).
 *
 * Storage: a single IndexedDB object store keyed by autoincrement id with
 *   { url, method, body, idempotencyKey, queuedAt }
 */

const DB_NAME = 'fin.app.offline';
const DB_VERSION = 1;
const STORE = 'queue';

export interface QueuedRequest {
  id?: number;
  url: string;        // path relative to api base
  method: 'PATCH' | 'POST' | 'PUT' | 'DELETE';
  body: string;       // JSON-stringified
  idempotencyKey: string;
  queuedAt: number;
  label?: string;     // e.g. "status: accepted" — for UI/debug
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueue(req: Omit<QueuedRequest, 'id' | 'queuedAt'>): Promise<number> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const full: Omit<QueuedRequest, 'id'> = { ...req, queuedAt: Date.now() };
    const r = store.add(full);
    r.onsuccess = () => resolve(r.result as number);
    r.onerror = () => reject(r.error);
  });
}

export async function listQueue(): Promise<QueuedRequest[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const r = tx.objectStore(STORE).getAll();
    r.onsuccess = () => resolve(r.result as QueuedRequest[]);
    r.onerror = () => reject(r.error);
  });
}

async function remove(id: number): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const r = tx.objectStore(STORE).delete(id);
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
  });
}

export { remove };

export function isNetworkError(e: unknown): boolean {
  if (!e) return false;
  // fetch throws TypeError on offline / DNS / CORS-pre-flight network failures.
  if (e instanceof TypeError) return true;
  // Our ApiError exposes a numeric status; treat 5xx and 0 as transient.
  const status = (e as { status?: number }).status;
  if (typeof status === 'number' && (status === 0 || status >= 500)) return true;
  return false;
}
